import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { _, TranslateModule } from '@ngx-translate/core';
import { AddressComponent } from 'src/app/components/address/address.component';
import { SearchPlaceComponent, SearchResult } from 'src/app/components/search-place/search-place.component';
import { AddressService } from 'src/app/services/address.service';
import { AuthService } from 'src/app/services/auth.service';
import { AlertUtils } from 'src/app/utils/alert.utils';
import { LoadingUtils } from 'src/app/utils/loading.utils';
import { AddressModalComponent } from '../../../../modals/address-modal/address-modal.component';
import { AddressData } from '../../../../models/create-offer/address-data';
import { CreateOfferData } from '../../../../models/create-offer/create-offer-data';
import { Address } from '../../../../models/user';
import { PlaceService } from '../../../../services/place.service';

@Component({
    selector: 'app-pick-addresses',
    templateUrl: './pick-addresses.component.html',
    styleUrls: ['./pick-addresses.component.scss'],
    imports: [CommonModule, TranslateModule, CommonModule, AddressComponent, SearchPlaceComponent],
})
export class PickAddressesComponent implements OnInit, AfterViewInit {
    @Output() nextStep = new EventEmitter<CreateOfferData>();
    @Input() data: CreateOfferData;
    /** When set (e.g. offer edit), pre-select this address instead of starting empty */
    @Input() initialAddress: Address | null = null;
    @Input() useLocationModal = false;
    @Input() isEdit = false;
    @ViewChild('mapElement', { static: false }) mapElement: ElementRef;

    address: Address;
    error = false;
    map: google.maps.Map;
    circle: google.maps.Circle;
    mapInitialized = false;

    constructor(
        private readonly authService: AuthService,
        private readonly modalCtrl: ModalController,
        private readonly placeService: PlaceService,
        private readonly ngZone: NgZone,
        private readonly addressService: AddressService,
    ) {}

    ngOnInit() {
        this.address = this.data?.addressData?.address ?? this.initialAddress ?? null;
        this.mapInitialized = false;
        this.error = false;

        // Clear any existing map references
        if (this.circle) {
            this.circle.setMap(null);
            this.circle = null;
        }
        if (this.map) {
            this.map = null;
        }
    }

    ngAfterViewInit() {
        if (this.useLocationModal && this.address?.lat && this.address?.lng) {
            setTimeout(() => {
                if (!this.mapInitialized) {
                    this.initMap();
                } else {
                    this.updateMapLocation();
                }
            }, 100);
        }
    }

    selectAddress() {
        // Only open modal for regular address mode
        if (!this.useLocationModal) {
            this.modalCtrl
                .create({
                    component: AddressModalComponent,
                    componentProps: {
                        isProfile: false,
                        type: 'user',
                        address: this.address,
                    },
                })
                .then(async (modal) => {
                    await modal.present();
                    modal.onDidDismiss().then((event) => {
                        if (event?.data?.address) {
                            this.address = event.data.address;
                            this.error = false;
                        }
                    });
                });
        }
    }

    setLocationResult(result: SearchResult) {
        this.ngZone.run(() => {
            // Create an Address object from the search result
            this.address = new Address({
                type: 'personal',
                lat: result.lat.toFixed(7),
                lng: result.lng.toFixed(7),
                city: result.city || '',
                country: result.country || '',
                region: result.region || '',
                department: result.department || '',
                street: result.street || '',
                streetNumber: result.streetNumber || '',
                zip: result.zip || '',
                firstname: '',
                lastname: '',
                mobilePhone: '',
                companyName: result.name || '',
                deliveryInstructions: '',
                profileAddress: false,
            });

            this.error = false;

            // Initialize or update map
            setTimeout(() => {
                if (!this.mapInitialized) {
                    this.initMap();
                } else {
                    this.updateMapLocation();
                }
            }, 100);
        });
    }

    clearLocation() {
        this.address = null;
        this.mapInitialized = false;
        this.error = false;
    }

    private initMap() {
        if (!this.address?.lat || !this.address?.lng || !this.mapElement) {
            return;
        }

        this.placeService.loadApi('maps').then(() => {
            const center = {
                lat: parseFloat(this.address.lat),
                lng: parseFloat(this.address.lng),
            };

            this.map = new google.maps.Map(this.mapElement.nativeElement, {
                center,
                zoom: 14,
                zoomControl: false,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                mapTypeId: google.maps.MapTypeId.ROADMAP,
            });

            const platformColor = getComputedStyle(document.documentElement).getPropertyValue('--platform-color').trim();

            this.circle = new google.maps.Circle({
                strokeColor: platformColor,
                strokeOpacity: 0.8,
                strokeWeight: 1,
                fillColor: platformColor,
                fillOpacity: 0.35,
                map: this.map,
                center,
                radius: 500,
            });

            this.mapInitialized = true;
        });
    }

    private updateMapLocation() {
        if (!this.map || !this.address?.lat || !this.address?.lng) {
            return;
        }

        const center = {
            lat: parseFloat(this.address.lat),
            lng: parseFloat(this.address.lng),
        };

        this.map.setCenter(center);
        this.circle.setCenter(center);
    }

    getLocationSummary(): string {
        if (!this.address) {
            return '';
        }

        const parts = [];

        // Add street address if available
        if (this.address.streetNumber && this.address.street) {
            parts.push(`${this.address.streetNumber} ${this.address.street}`);
        } else if (this.address.street) {
            parts.push(this.address.street);
        }

        // Add city
        if (this.address.city) {
            parts.push(this.address.city);
        }

        // Add region
        if (this.address.region) {
            parts.push(this.address.region);
        }

        // Add country if available
        if (this.address.country) {
            parts.push(this.address.country);
        }

        return parts.join(', ');
    }

    async submit() {
        if (!this.address) {
            this.error = true;
            return;
        }

        const saveAndNext = (address: Address) => {
            // Ensure addressData is initialized
            if (!this.data.addressData) {
                this.data.addressData = new AddressData();
            }

            this.data.addressData.address = address;
            this.nextStep.emit(this.data);
        };

        // If useLocationModal is true, create a new address on API unless one already exists
        if (this.useLocationModal) {
            if (this.address.id) {
                saveAndNext(this.address);
                return;
            }

            await LoadingUtils.show();
            const userId = this.authService.userLoggedIn.value.id;
            this.addressService.addAddress(userId, this.address).subscribe({
                next: (address) => {
                    LoadingUtils.hide();
                    saveAndNext(address);
                },
                error: (error) => {
                    LoadingUtils.hide();
                    AlertUtils.showError(_('An error occurred'));
                    console.error('Error adding address:', error);
                },
            });
        } else {
            saveAndNext(this.address);
        }
    }
}
